import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from 'src/shared/components/ui/button';
import SnackbarAlert from 'src/shared/components/snackbar-alert/SnackbarAlert';
import type { AlertSeverity } from 'src/shared/components/ui/alert';
import useAuthService from 'src/shared/hooks/useAuthService';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import {
    hasSiteStatusChangedSince,
    countNeedsSync,
    useGetRiskInsightsSiteStatusQuery,
    useSubmitRiskInsightsMutation,
    type RiskInsightsRunScope,
    type SiteStatusItem,
} from 'src/app/portfolio/state/api/riskInsightsRefreshEngineSlice';
import RunRefreshModal from './RunRefreshModal';

export const SITE_STATUS_POLL_INTERVAL_MS = 60_000;

type SnackbarState = { open: boolean; message: string; severity: AlertSeverity };

const extractErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object' && 'data' in err) {
        const data = (err as { data?: { error?: string } }).data;
        if (data?.error) {
            return data.error;
        }
    }
    return 'Could not start the refresh run.';
};

const RunRefreshButton = () => {
    const { isCurrentUserSuperAdmin } = useAuthService();
    const { currentCustomerId } = useCustomer();

    const [isModalOpen, setModalOpen] = useState(false);
    const [submittedSnapshot, setSubmittedSnapshot] = useState<ReadonlyArray<SiteStatusItem> | null>(null);
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

    const { currentData, isLoading, refetch } = useGetRiskInsightsSiteStatusQuery(
        { businessCentralId: currentCustomerId ?? '' },
        { pollingInterval: SITE_STATUS_POLL_INTERVAL_MS, skip: !isCurrentUserSuperAdmin || !currentCustomerId }
    );

    const [submitRun, { isLoading: isSubmitting }] = useSubmitRiskInsightsMutation();

    if (!isCurrentUserSuperAdmin) {
        return null;
    }

    const items = currentData?.items ?? [];
    const awaitingRun = submittedSnapshot !== null && !hasSiteStatusChangedSince(submittedSnapshot, items);
    const disabled = isLoading || items.length === 0 || awaitingRun;

    const handleSubmit = async (scope: RiskInsightsRunScope) => {
        if (!currentCustomerId) {
            return;
        }
        try {
            const { run_id } = await submitRun({ businessCentralId: currentCustomerId, scope }).unwrap();
            setSubmittedSnapshot(items);
            setModalOpen(false);
            setSnackbar({ open: true, message: `Refresh run started (${run_id})`, severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: extractErrorMessage(err), severity: 'error' });
        }
    };

    const handleOpen = () => {
        // The 60s poll can be stale right after a site edit — refresh the counts the moment the popup opens.
        refetch();
        setModalOpen(true);
    };

    return (
        <>
            <Button variant='outline' disabled={disabled} onClick={handleOpen} data-testid='run-refresh-button'>
                <span>{awaitingRun ? 'Run started' : 'Refresh risk data'}</span>
                <RefreshCw className='w-4 h-4 ml-2' />
            </Button>
            <RunRefreshModal
                open={isModalOpen}
                onOpenChange={setModalOpen}
                totalSiteCount={items.length}
                needsSyncCount={countNeedsSync(items)}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
            />
            <SnackbarAlert
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                dataTestId='run-refresh-snackbar'
            />
        </>
    );
};

export default RunRefreshButton;
